// ============================================================================
// MILESTONE PICKER
//
// The delivery-phase chip on a story or feature card. Unset it is a dashed
// ghost chip that only appears on hover, so untagged cards stay uncluttered;
// set it is a solid chip showing the phase name.
//
// One milestone per item, deliberately. Delivery phases are exclusive, and an
// array would make "what ships in Phase 1" ambiguous.
// ============================================================================

import React, { useRef, useState, useEffect } from 'react';
import { Milestone as MilestoneIcon, Plus, X, Check } from 'lucide-react';
import { Milestone } from '../../types/raid';

export interface MilestonePickerProps {
  milestones: Milestone[];
  value?: string;
  onChange: (milestoneId: string | null) => void;
  disabled?: boolean;
  /** Compact suits table rows; default suits cards. */
  size?: 'sm' | 'md';
}

export function MilestonePicker({
  milestones, value, onChange, disabled, size = 'sm',
}: Readonly<MilestonePickerProps>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const current = milestones.find(m => m.milestoneId === value);
  const pad = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';

  if (milestones.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        title={current ? `Delivery phase: ${current.name}` : 'Assign a delivery phase'}
        className={
          current
            ? `flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 text-primary font-semibold transition-colors hover:bg-primary/20 ${pad}`
            // Hidden until hover so untagged cards stay clean.
            : `flex items-center gap-1 rounded-full border border-dashed border-slate-300 text-slate-400 font-semibold transition-all hover:border-primary hover:text-primary opacity-0 group-hover:opacity-100 ${pad}`
        }
      >
        {current ? <MilestoneIcon size={9} /> : <Plus size={9} />}
        {current ? current.name : 'Phase'}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[180px] max-h-60 overflow-y-auto">
          {milestones.map(m => (
            <button
              key={m.milestoneId}
              type="button"
              onClick={() => { onChange(m.milestoneId); setOpen(false); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <span className="truncate">{m.name}</span>
              {m.milestoneId === value && <Check size={12} className="text-primary shrink-0" />}
            </button>
          ))}
          {value && (
            <>
              <div className="h-px bg-slate-100 my-1" />
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-slate-500 hover:bg-slate-50 transition-colors"
              >
                <X size={12} /> Clear phase
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
